import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CheckCircle2, Package, ChevronDown, ChevronUp, Banknote } from 'lucide-react';
import { useVatRate, useActiveWithholdingRates } from '@/hooks/finance/useTaxSettings';
import { useUpdateProcurementStatus } from '@/hooks/use-procurement';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { sendTelegramNotification } from '@/services/telegram/send';
import { formatApprovedProcurementNotification } from '@/services/telegram/formatters';
import { getProcurementById } from '@/services/procurementService';
import { calculateNetPayableKg, calculateAveragePricePerUnit, calculateNetPayableAmount } from '@/utils/procurementCalculations';
import { supabase } from '@/integrations/supabase/client';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { format } from 'date-fns';

// Standard department and category for all procurement expenses
const SOURCING_DEPARTMENT_ID = '6b863514-6646-4a6e-aca4-d53d40ef0862';
const PURCHASE_CATEGORY_ID = 'edc557d1-4e7b-40a9-9914-c09bffb85ef9'; // COGS-SRF-PUR

interface ApproveConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  procurementId: string;
  onSuccess?: () => void;
}

const ApproveConfirmationDialog: React.FC<ApproveConfirmationDialogProps> = ({
  isOpen,
  onClose,
  procurementId,
  onSuccess
}) => {
  const [isApproving, setIsApproving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [procurementData, setProcurementData] = useState<any>(null);
  const [paidFromAccountId, setPaidFromAccountId] = useState<string>('');
  const [actualPaymentDate, setActualPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [itemsExpanded, setItemsExpanded] = useState(false);
  const [vatMode, setVatMode] = useState<'none' | 'add' | 'deduct'>('none');
  const [withholdingApplicable, setWithholdingApplicable] = useState(false);
  const [selectedWithholdingRateId, setSelectedWithholdingRateId] = useState<string>('');
  const [paymentReference, setPaymentReference] = useState('');
  
  const { user } = useAuth();
  const updateProcurementMutation = useUpdateProcurementStatus();
  const { data: bankAccounts, isLoading: bankAccountsLoading } = useBankAccounts(true);
  const { data: vatRate } = useVatRate();
  const { data: withholdingRates } = useActiveWithholdingRates();

  // Fetch procurement data when dialog opens
  useEffect(() => {
    if (isOpen && procurementId) {
      fetchProcurementData();
    }
  }, [isOpen, procurementId]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setPaidFromAccountId('');
      setActualPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentNotes('');
      setItemsExpanded(false);
      setVatMode('none');
      setWithholdingApplicable(false);
      setSelectedWithholdingRateId('');
      setPaymentReference('');
    }
  }, [isOpen]);

  // Set default withholding rate when rates load
  useEffect(() => {
    if (withholdingRates?.length && !selectedWithholdingRateId) {
      const defaultRate = withholdingRates.find(r => r.is_default);
      if (defaultRate) setSelectedWithholdingRateId(defaultRate.id);
    }
  }, [withholdingRates, selectedWithholdingRateId]);

  // Tax calculation logic
  const taxCalc = useMemo(() => {
    const hasAdvancePayment = procurementData?.finance?.has_advance_payment === true;
    const advancePaymentAmount = procurementData?.finance?.advance_payment_amount || 0;
    const netPayableAmount = procurementData ? calculateNetPayableAmount(procurementData) : 0;
    const rawAmount = hasAdvancePayment ? advancePaymentAmount : netPayableAmount;

    let baseAmount = rawAmount;
    let vatAmount = 0;
    const effectiveVatRate = vatRate ?? 15;

    if (vatMode === 'add') {
      baseAmount = rawAmount;
      vatAmount = baseAmount * (effectiveVatRate / 100);
    } else if (vatMode === 'deduct') {
      baseAmount = rawAmount / (1 + effectiveVatRate / 100);
      vatAmount = rawAmount - baseAmount;
    } else {
      baseAmount = rawAmount;
      vatAmount = 0;
    }

    const subtotal = baseAmount + vatAmount;

    let withholdingRate = 0;
    let withholdingAmount = 0;
    if (withholdingApplicable && selectedWithholdingRateId) {
      const selectedRate = withholdingRates?.find(r => r.id === selectedWithholdingRateId);
      withholdingRate = selectedRate?.rate || 0;
      withholdingAmount = baseAmount * (withholdingRate / 100);
    }

    const netPayment = subtotal - withholdingAmount;

    return { baseAmount, vatAmount, subtotal, withholdingRate, withholdingAmount, netPayment, effectiveVatRate };
  }, [procurementData, vatMode, vatRate, withholdingApplicable, selectedWithholdingRateId, withholdingRates]);

  const fetchProcurementData = async () => {
    setIsLoading(true);
    try {
      const data = await getProcurementById(procurementId);
      setProcurementData(data);
    } catch (error) {
      console.error('Error fetching procurement data:', error);
      toast({
        title: "Error",
        description: "Failed to load procurement details.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveAndPay = async () => {
    if (!user || !procurementData) return;
    
    if (!paidFromAccountId) {
      toast({
        title: "Required Field",
        description: "Please select a bank account for payment.",
        variant: "destructive",
      });
      return;
    }

    if (!paymentReference.trim()) {
      toast({
        title: "Required Field",
        description: "Please enter a payment reference.",
        variant: "destructive",
      });
      return;
    }
    
    setIsApproving(true);
    try {
      console.log('Starting approve & pay process for procurement:', procurementId);
      
      // Determine next status based on payment term type
      const paymentTermType = procurementData.finance?.payment_term_type || 'upfront';
      let nextStatus: string;
      let successMessage: string;

      if (paymentTermType === 'term') {
        // For term payments, finance approval completes the procurement 
        // (quality and warehouse already confirmed before finance)
        nextStatus = 'completed';
        successMessage = 'Procurement approved, paid, and completed.';
      } else {
        // For upfront payments, send to quality inspection first
        nextStatus = 'pending_quality';
        successMessage = 'Procurement approved, paid, and sent to Quality Inspection.';
      }

      console.log(`Payment term type: ${paymentTermType}, setting status to: ${nextStatus}`);
      
      // Calculate and store net payable values at approval time
      const netPayableKg = calculateNetPayableKg(procurementData);
      const avgPricePerUnit = calculateAveragePricePerUnit(procurementData);
      const netPayableAmount = calculateNetPayableAmount(procurementData);

      console.log('Calculated net payable values:', {
        netPayableKg,
        avgPricePerUnit,
        netPayableAmount
      });

      // Step 1: Generate expense number
      const { data: expenseNumber, error: numberError } = await supabase
        .rpc('generate_expense_number');
      
      if (numberError || !expenseNumber) {
        console.error('Error generating expense number:', numberError);
        throw new Error('Failed to generate expense number');
      }

      console.log('Generated expense number:', expenseNumber);

      const hasAdvancePayment = procurementData.finance?.has_advance_payment === true;
      const advancePaymentAmount = procurementData.finance?.advance_payment_amount || 0;

      // Determine expense amount: advance payment only or full amount
      const expenseAmount = hasAdvancePayment ? advancePaymentAmount : netPayableAmount;
      const expenseDescription = hasAdvancePayment
        ? `Advance payment - Procurement: ${procurementData.product_name || 'Product'} from ${procurementData.source_name || 'Supplier'}`
        : `Procurement: ${procurementData.product_name || 'Product'} from ${procurementData.source_name || 'Supplier'}`;

      // Step 2: Create expense record with tax fields
      const { data: expense, error: expenseError } = await supabase
        .from('expenses')
        .insert({
          expense_number: expenseNumber,
          expense_date: actualPaymentDate,
          description: expenseDescription,
          vendor_name: procurementData.source_name || 'Unknown Supplier',
          reference_number: `PROC-${procurementId.slice(0, 8)}`,
          amount: taxCalc.baseAmount,
          currency: 'ETB',
          vat_rate: vatMode !== 'none' ? taxCalc.effectiveVatRate : 0,
          vat_amount: taxCalc.vatAmount,
          withholding_rate: taxCalc.withholdingRate,
          withholding_amount: taxCalc.withholdingAmount,
          total_amount: taxCalc.netPayment,
          net_payment: taxCalc.netPayment,
          payment_status: 'paid',
          payment_date: actualPaymentDate,
          payment_reference: paymentReference.trim(),
          created_by: user.id,
          approved_by: user.id,
          approved_at: new Date().toISOString(),
          department_id: SOURCING_DEPARTMENT_ID,
          category_id: PURCHASE_CATEGORY_ID,
          bank_account_id: paidFromAccountId
        })
        .select('id')
        .single();

      if (expenseError) {
        console.error('Error creating expense:', expenseError);
        throw new Error('Failed to create expense record.');
      }

      console.log('Created expense:', expense);

      // Get selected bank account name for denormalization
      const selectedAccount = bankAccounts?.find(a => a.id === paidFromAccountId);

      // Step 3: Update procurement_finance with approved values AND payment confirmation
      const financeUpdate: any = {
        // Approval fields (always set)
        approved_by: user.id,
        finance_approved_at: new Date().toISOString(),
        approved_net_payable_amount: netPayableAmount,
        approved_net_payable_kg: netPayableKg,
        approved_price_per_unit: avgPricePerUnit,
      };

      if (hasAdvancePayment) {
        // For advance payment: only mark advance as confirmed, NOT full payment
        financeUpdate.advance_payment_confirmed = true;
        financeUpdate.advance_payment_confirmed_by = user.id;
        financeUpdate.advance_payment_confirmed_at = new Date().toISOString();
        financeUpdate.advance_payment_date = actualPaymentDate;
        financeUpdate.advance_payment_notes = paymentNotes || null;
        financeUpdate.advance_paid_from_account_id = paidFromAccountId;
        // Don't set expense_id yet — will be set when remaining payment creates the final expense
      } else {
        // For regular (non-advance) payment: mark full payment confirmed
        financeUpdate.payment_confirmed = true;
        financeUpdate.payment_confirmed_by = user.id;
        financeUpdate.payment_confirmed_at = new Date().toISOString();
        financeUpdate.actual_payment_date = actualPaymentDate;
        financeUpdate.payment_confirmation_notes = paymentNotes || null;
        financeUpdate.expense_id = expense.id;
        financeUpdate.paid_from_account_id = paidFromAccountId;
        financeUpdate.paid_from_account_name = selectedAccount?.name || null;
      }

      const { error: financeError } = await supabase
        .from('procurement_finance')
        .update(financeUpdate)
        .eq('procurement_id', procurementId);

      if (financeError) {
        console.error('Error updating procurement finance:', financeError);
        throw financeError;
      }

      // Step 4: Update procurement status
      await updateProcurementMutation.mutateAsync({
        id: procurementId,
        status: nextStatus
      });

      console.log('Procurement status updated successfully');

      // Step 5: Fetch complete enriched procurement data for notification
      const enrichedProcurement = await getProcurementById(procurementId);
      
      if (enrichedProcurement) {
        // Send Telegram notification
        const notificationMessage = formatApprovedProcurementNotification(enrichedProcurement, user);
        await sendTelegramNotification(notificationMessage, procurementId);
      }

      toast({
        title: "Procurement Approved & Paid",
        description: successMessage,
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error approving procurement:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to approve procurement. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsApproving(false);
    }
  };

  const items = procurementData?.items || [];
  const quality = procurementData?.quality;

  // Calculate preview of net payable
  const previewNetPayableKg = procurementData ? calculateNetPayableKg(procurementData) : 0;
  const previewNetPayableAmount = procurementData ? calculateNetPayableAmount(procurementData) : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="h-5 w-5 text-primary" />
            Approve & Pay Procurement
          </DialogTitle>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : procurementData ? (
          <div className="space-y-4">
            {/* Payment Summary */}
            <div className="bg-muted p-4 rounded-md">
              <h4 className="font-medium mb-2">Payment Summary</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span><span className="font-medium">Product:</span> {procurementData.product_name || 'N/A'}</span>
                  {items.length > 1 && (
                    <Badge variant="outline" className="text-xs">
                      <Package className="h-3 w-3 mr-1" />
                      {items.length} items
                    </Badge>
                  )}
                </div>
                <div><span className="font-medium">Source:</span> {procurementData.source_name || 'N/A'}</div>
                <div><span className="font-medium">Original Quantity:</span> {procurementData.total_quantity || 0} kg</div>
                
                {/* Quality adjustments if any */}
                {quality && (quality.compensation_kg > 0 || quality.preliminary_compensation_kg > 0) && (
                  <div className="text-destructive">
                    <span className="font-medium">Compensation:</span> -{quality.compensation_kg || quality.preliminary_compensation_kg || 0} kg
                  </div>
                )}
                
                <div className="pt-2 border-t border-border mt-2">
                  <div className="flex justify-between font-medium">
                    <span>Net Payable:</span>
                    <span>{previewNetPayableKg.toFixed(2)} kg</span>
                  </div>
                  <div className="flex justify-between font-medium text-lg text-primary">
                    <span>{procurementData.finance?.has_advance_payment ? 'Total Amount:' : 'Amount:'}</span>
                    <span>Br {previewNetPayableAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  {procurementData.finance?.has_advance_payment && (
                    <div className="flex justify-between font-medium text-lg text-green-600 mt-1">
                      <span>Advance Payment (paying now):</span>
                      <span>Br {(procurementData.finance.advance_payment_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Item-level breakdown for multi-product procurements */}
              {items.length > 1 && (
                <Collapsible open={itemsExpanded} onOpenChange={setItemsExpanded} className="mt-3">
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full justify-between p-2 h-auto">
                      <span className="text-xs">View item breakdown</span>
                      {itemsExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <div className="bg-background rounded-md p-2 space-y-1">
                      {items.map((item: any) => {
                        const itemQuality = item.item_quality;
                        const itemWarehouse = item.item_warehouse;
                        const receivedQty = itemWarehouse?.received_quantity_kg ?? item.quantity;
                        const compensation = itemQuality?.compensation_kg || itemQuality?.preliminary_compensation_kg || 0;
                        const netPayableKg = receivedQty - compensation;
                        const itemNetAmount = netPayableKg * (item.price_per_unit || 0);
                        
                        return (
                          <div key={item.id} className="flex justify-between items-center text-xs border-b border-border/50 pb-1 last:border-0 last:pb-0">
                            <div>
                              <span className="font-medium">{item.product_name}</span>
                              <span className="text-muted-foreground ml-1">
                                ({receivedQty.toFixed(1)} kg @ Br{item.price_per_unit || 0})
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {itemQuality?.quality_grade && (
                                <Badge variant="outline" className="text-xs h-5">
                                  {itemQuality.quality_grade}
                                </Badge>
                              )}
                              {compensation > 0 && (
                                <span className="text-destructive">-{compensation.toFixed(1)}kg</span>
                              )}
                              <span className="font-medium text-primary">
                                Br{itemNetAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>

            {/* Payment Term Badge */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Payment Type:</span>
              <Badge variant={procurementData.finance?.payment_term_type === 'term' ? 'secondary' : 'default'}>
                {procurementData.finance?.payment_term_type === 'term' ? 'Term Payment' : 'Upfront Payment'}
              </Badge>
            </div>

            {/* Supplier Bank Account (if alternative) */}
            {procurementData.finance?.use_different_bank && procurementData.finance?.bank_name && (
              <div className="bg-accent/50 border border-border p-3 rounded-md">
                <h4 className="font-medium mb-1 text-sm">🏦 Pay To (Alternative Account)</h4>
                <div className="space-y-0.5 text-xs text-muted-foreground">
                  <div><span className="font-medium text-foreground">Bank:</span> {procurementData.finance.bank_name}</div>
                  {procurementData.finance.bank_account && (
                    <div><span className="font-medium text-foreground">Account:</span> {procurementData.finance.bank_account}</div>
                  )}
                  {procurementData.finance.account_holder_name && (
                    <div><span className="font-medium text-foreground">Holder:</span> {procurementData.finance.account_holder_name}</div>
                  )}
                </div>
              </div>
            )}

            {/* Company Bank Account Selection */}
            <div className="space-y-2">
              <Label htmlFor="paidFromAccount">Paid From Account *</Label>
              <Select value={paidFromAccountId} onValueChange={setPaidFromAccountId}>
                <SelectTrigger id="paidFromAccount">
                  <SelectValue placeholder={bankAccountsLoading ? "Loading accounts..." : "Select company bank account"} />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts?.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.name} {account.bank_name ? `(${account.bank_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* VAT Mode */}
            <div className="space-y-2">
              <Label>VAT Application</Label>
              <RadioGroup value={vatMode} onValueChange={(v) => setVatMode(v as 'none' | 'add' | 'deduct')} className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="none" id="vat-none-proc" />
                  <Label htmlFor="vat-none-proc" className="font-normal text-sm">No VAT</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="add" id="vat-add-proc" />
                  <Label htmlFor="vat-add-proc" className="font-normal text-sm">Add VAT</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="deduct" id="vat-deduct-proc" />
                  <Label htmlFor="vat-deduct-proc" className="font-normal text-sm">Deduct VAT</Label>
                </div>
              </RadioGroup>
            </div>

            {/* Withholding Tax */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Apply Withholding Tax</Label>
                <Switch checked={withholdingApplicable} onCheckedChange={setWithholdingApplicable} />
              </div>
              {withholdingApplicable && withholdingRates && withholdingRates.length > 0 && (
                <Select value={selectedWithholdingRateId} onValueChange={setSelectedWithholdingRateId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select withholding rate" />
                  </SelectTrigger>
                  <SelectContent>
                    {withholdingRates.map((rate) => (
                      <SelectItem key={rate.id} value={rate.id}>
                        {rate.name} ({rate.rate}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Payment Reference (mandatory) */}
            <div className="space-y-2">
              <Label htmlFor="paymentRefProc">Payment Reference <span className="text-destructive">*</span></Label>
              <Input
                id="paymentRefProc"
                placeholder="Enter payment reference..."
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
              />
            </div>

            {/* Financial Breakdown */}
            {(vatMode !== 'none' || withholdingApplicable) && (
              <div className="bg-muted p-3 rounded-md text-sm space-y-1">
                <h4 className="font-medium mb-1">Financial Breakdown</h4>
                <div className="flex justify-between">
                  <span>Base Amount:</span>
                  <span>Br {taxCalc.baseAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
                {vatMode !== 'none' && (
                  <div className="flex justify-between">
                    <span>VAT ({taxCalc.effectiveVatRate}%):</span>
                    <span>Br {taxCalc.vatAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {vatMode !== 'none' && (
                  <div className="flex justify-between">
                    <span>Subtotal (incl. VAT):</span>
                    <span>Br {taxCalc.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                {withholdingApplicable && taxCalc.withholdingAmount > 0 && (
                  <div className="flex justify-between text-destructive">
                    <span>Withholding ({taxCalc.withholdingRate}%):</span>
                    <span>-Br {taxCalc.withholdingAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold border-t border-border pt-1 mt-1">
                  <span>Net Payment:</span>
                  <span>Br {taxCalc.netPayment.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            )}

            {/* Payment Date */}
            <div className="space-y-2">
              <Label htmlFor="paymentDate">Payment Date</Label>
              <Input
                id="paymentDate"
                type="date"
                value={actualPaymentDate}
                onChange={(e) => setActualPaymentDate(e.target.value)}
              />
            </div>

            {/* Payment Notes */}
            <div className="space-y-2">
              <Label htmlFor="paymentNotes">Payment Notes (Optional)</Label>
              <Textarea
                id="paymentNotes"
                placeholder="Add any notes about this payment..."
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                rows={2}
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Failed to load procurement details.
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isApproving}>
            Cancel
          </Button>
          <Button 
            onClick={handleApproveAndPay} 
            disabled={isApproving || isLoading || !paidFromAccountId || !paymentReference.trim()}
            className="gap-2"
          >
            <CheckCircle2 className="h-4 w-4" />
            {isApproving ? 'Processing...' : 'Approve & Pay'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApproveConfirmationDialog;
